import * as React from 'react'
import { Table, Icon, Tooltip, message } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { API, ONComplianceReportRequest, IONFederalPreviewReport } from '@getgreenline/homi-shared'
import { Link } from 'react-router'
import { Routes } from '../../../../../../constants/Routes'
import { inject } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import '../compliancePreviewTable.scss'
import { CustomFilterDropdown } from '../../../../DashboardInvoices/InvoiceDetail/Main/CustomFilterDropdown'
import Highlighter from 'react-highlight-words'
import { ComplianceStore } from '../../ComplianceStore'
import { parseErrorMsg, GrPrice } from '../../../../../../utilities/helpers'
import { isNumber } from 'util'
import { PreviewTableTitle, NegativeProduct, NegativeProductItem } from './PreviewTableTitle'
import { Track } from '../../../../../../Track'

interface Props {
  complianceStore: ComplianceStore
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

enum ToggleableColumnIndices {
  OPENING = 'Opening',
  QUANTITY_PURCHASED = 'Quantity purchased',
  RETURNS_FROM_CUSTOMERS = 'Returns from customers',
  OTHER_ADDITION = 'Other additions',
  QUANTITY_SOLD = 'Quantity sold',
  QUANTITY_DESTROYED = 'Quantity destroyed',
  QUANTITY_LOST_THEFT = 'Quantity Lost / theft',
  RETURNS_TO_AGENCY = 'Returns to OCS',
  OTHER_REDUCTION = 'Other reductions',
  CLOSING = 'Closing',
}

type CompliancePreviewQuantities = Omit<
  CompliancePreviewTableRecord,
  | 'key'
  | 'productId'
  | 'parentProductId'
  | 'productName'
  | 'productSKU'
  | 'productBarcode'
  | 'isProductDeleted'
>

const columnRecordMap: { [key in keyof CompliancePreviewQuantities]: ToggleableColumnIndices } = {
  openingInventoryUnit: ToggleableColumnIndices.OPENING,
  openingInventoryValue: ToggleableColumnIndices.OPENING,
  quantityPurchasedUnit: ToggleableColumnIndices.QUANTITY_PURCHASED,
  quantityPurchasedValue: ToggleableColumnIndices.QUANTITY_PURCHASED,
  returnsFromCustomersUnit: ToggleableColumnIndices.RETURNS_FROM_CUSTOMERS,
  returnsFromCustomersValue: ToggleableColumnIndices.RETURNS_FROM_CUSTOMERS,
  otherAdditionsUnit: ToggleableColumnIndices.OTHER_ADDITION,
  otherAdditionsValue: ToggleableColumnIndices.OTHER_ADDITION,
  quantitySoldUnit: ToggleableColumnIndices.QUANTITY_SOLD,
  quantitySoldValue: ToggleableColumnIndices.QUANTITY_SOLD,
  quantityDestroyedUnit: ToggleableColumnIndices.QUANTITY_DESTROYED,
  quantityDestroyedValue: ToggleableColumnIndices.QUANTITY_DESTROYED,
  quantityLostUnit: ToggleableColumnIndices.QUANTITY_LOST_THEFT,
  quantityLostValue: ToggleableColumnIndices.QUANTITY_LOST_THEFT,
  returnsToOCSUnit: ToggleableColumnIndices.RETURNS_TO_AGENCY,
  returnsToOCSValue: ToggleableColumnIndices.RETURNS_TO_AGENCY,
  otherReductionsUnit: ToggleableColumnIndices.OTHER_REDUCTION,
  otherReductionsValue: ToggleableColumnIndices.OTHER_REDUCTION,
  closingInventoryUnit: ToggleableColumnIndices.CLOSING,
  closingInventoryValue: ToggleableColumnIndices.CLOSING,
}

interface CompliancePreviewTableRecord extends IONFederalPreviewReport {
  key: number
}

interface State {
  searchedProductName: string
  searchedSKU: string
  searchedBarcode: string
  searchedColumn: keyof CompliancePreviewTableRecord | undefined
  previewReport: IONFederalPreviewReport[] | undefined
  isLoading: boolean
  mappedColumns: Map<string, boolean>
  windowWidth: number
}

type TSearchTextKey = 'searchedProductName' | 'searchedSKU' | 'searchedBarcode'

@inject('currentCompanyStore')
export class ONCompliancePreviewTable extends React.Component<Props & InjectedProps, State> {
  private qtyWidth = 90

  private wholesaleValueColumnWidth = 160

  private retailValueColumnWidth = 130

  private widthBreakpoint = 1232

  private company = this.props.currentCompanyStore!.company!

  private negativeProducts: NegativeProduct = {}

  constructor(props: Props & InjectedProps) {
    super(props)

    const mappedColumns = new Map<string, boolean>()

    Object.keys(ToggleableColumnIndices).forEach((columnKey) =>
      mappedColumns.set(ToggleableColumnIndices[columnKey], true),
    )

    this.state = {
      searchedProductName: '',
      searchedSKU: '',
      searchedBarcode: '',
      searchedColumn: undefined,
      previewReport: undefined,
      isLoading: true,
      mappedColumns,
      windowWidth: window.innerWidth,
    }
  }

  componentDidMount = async () => {
    const { month, year, locationIds } = this.props.complianceStore

    window.addEventListener('resize', () => this.setState({ windowWidth: window.innerWidth }))

    const params: ONComplianceReportRequest = {
      month,
      year,
      locationIds,
      reportType: 'federal',
    }

    Track.track('dashboard:compliance_complianceReportGenerated', params)

    try {
      const response = await API.getONComplianceReport<'preview', 'federal'>(
        this.company.id,
        'preview',
        params,
      )

      this.setState({ previewReport: response })
    } catch (error) {
      message.error(parseErrorMsg(error))
    } finally {
      this.setState({ isLoading: false })
    }
  }

  UNSAFE_componentWillMount = () =>
    window.removeEventListener('resize', () => this.setState({ windowWidth: window.innerWidth }))

  getValue = (quantity: number, price: number) => quantity * (price || 0)

  renderTitle = (title: string, sign: 'plus' | 'minus') => (
    <span>
      <Icon
        type={`${sign}-circle`}
        className='mr-1'
        style={{
          color: `var(--${sign === 'plus' ? 'green' : 'red'})`,
        }}
      />
      <span>{title}</span>
    </span>
  )

  renderCell = (type: 'unit' | 'value', amount: number) => {
    const children = type === 'unit' ? amount : GrPrice.convertDollarToDollar(amount)

    return amount >= 0
      ? { children }
      : {
          children,
          props: { className: 'cell-red' },
        }
  }

  applyFilter = (searchText: string, stringRecord: string) => {
    const lowerCasedStringRecord = stringRecord.toLowerCase()

    return searchText.split(' ').some((word: string) => {
      const lowerCasedWord = word.toLowerCase()

      return lowerCasedStringRecord.includes(lowerCasedWord)
    })
  }

  searchTable = (
    dataIndex: keyof CompliancePreviewTableRecord,
    searchTextKey: TSearchTextKey,
    searchText: string,
    confirm?: () => void,
  ) => {
    if (confirm) {
      confirm()

      const newState = {}
      newState[searchTextKey] = searchText
      newState['searchedColumn'] = dataIndex

      this.setState(newState)
    }
  }

  clearSearchResult = (searchTextKey: TSearchTextKey, clearFilters?: () => void) => {
    if (clearFilters) {
      clearFilters()

      const newState = {}
      newState[searchTextKey] = ''
      newState['searchedColumn'] = undefined

      this.setState(newState)
    }
  }

  get checkedColumnIndices(): string[] {
    const { mappedColumns } = this.state

    return [...mappedColumns.keys()].filter((key) => mappedColumns.get(key) === true)
  }

  get shouldColumnsBeFixed(): boolean {
    return this.checkedColumnIndices.length > 5 && this.state.windowWidth > this.widthBreakpoint
  }

  get baseColumns(): Array<ColumnProps<CompliancePreviewTableRecord>> {
    return [
      {
        title: 'Product',
        dataIndex: 'productName',
        key: 'productName',
        fixed: this.shouldColumnsBeFixed && 'left',
        width: 250,
        render: (text, record) => (
          <span>
            {record.isProductDeleted && (
              <Tooltip title='This product was deleted.'>
                <Icon className='mr-2' type='exclamation-circle' style={{ color: 'red' }} />
              </Tooltip>
            )}

            <Link
              to={Routes.EDIT_PRODUCT(this.company.id, record.parentProductId || record.productId)}
              target='_blank'
            >
              <Highlighter
                highlightClassName='highlighted-text'
                searchWords={[this.state.searchedProductName]}
                textToHighlight={record.productName || ''}
                autoEscape
              />
            </Link>
          </span>
        ),
        sorter: (r1, r2) => r1.productName.localeCompare(r2.productName),
        onFilter: (searchText: string, record) => this.applyFilter(searchText, record.productName),
        filterDropdown: (filterDropdownProps) => (
          <CustomFilterDropdown<CompliancePreviewTableRecord>
            placeholder='Search by name'
            dataIndex='productName'
            filterDropdownProps={filterDropdownProps}
            searchTable={(dataIndex, searchText, confirm) =>
              this.searchTable(dataIndex, 'searchedProductName', searchText, confirm)
            }
            clearSearchResult={(clearFilters) =>
              this.clearSearchResult('searchedProductName', clearFilters)
            }
          />
        ),
      },
      {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        fixed: this.shouldColumnsBeFixed && 'left',
        width: 150,
        render: (text, record) => (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={[this.state.searchedSKU]}
            textToHighlight={record.productSKU || ''}
            autoEscape
          />
        ),
        sorter: (r1, r2) => (r1.productSKU || '').localeCompare(r2.productSKU || ''),
        onFilter: (searchText: string, record) =>
          this.applyFilter(searchText, record.productSKU || ''),
        filterDropdown: (filterDropdownProps) => (
          <CustomFilterDropdown<CompliancePreviewTableRecord>
            placeholder='Search by SKU'
            dataIndex='productSKU'
            filterDropdownProps={filterDropdownProps}
            searchTable={(dataIndex, searchText, confirm) =>
              this.searchTable(dataIndex, 'searchedSKU', searchText, confirm)
            }
            clearSearchResult={(clearFilters) =>
              this.clearSearchResult('searchedSKU', clearFilters)
            }
          />
        ),
      },
      {
        title: 'Barcode',
        dataIndex: 'barcode',
        key: 'barcode',
        fixed: this.shouldColumnsBeFixed && 'left',
        width: 150,
        render: (text, record) => (
          <Highlighter
            highlightClassName='highlighted-text'
            searchWords={[this.state.searchedBarcode]}
            textToHighlight={record.productBarcode || ''}
            autoEscape
          />
        ),
        sorter: (r1, r2) => (r1.productBarcode || '').localeCompare(r2.productBarcode || ''),
        onFilter: (searchText: string, record) =>
          this.applyFilter(searchText, record.productBarcode || ''),
        filterDropdown: (filterDropdownProps) => (
          <CustomFilterDropdown<CompliancePreviewTableRecord>
            placeholder='Search by barcode'
            dataIndex='productBarcode'
            filterDropdownProps={filterDropdownProps}
            searchTable={(dataIndex, searchText, confirm) =>
              this.searchTable(dataIndex, 'searchedBarcode', searchText, confirm)
            }
            clearSearchResult={(clearFilters) =>
              this.clearSearchResult('searchedBarcode', clearFilters)
            }
          />
        ),
      },
    ]
  }

  get toggleableColumns(): Array<ColumnProps<CompliancePreviewTableRecord>> {
    const toggleableColumns: Array<ColumnProps<CompliancePreviewTableRecord>> = [
      {
        title: ToggleableColumnIndices.OPENING,
        dataIndex: ToggleableColumnIndices.OPENING,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'openingInventoryUnit',
            key: 'openingInventoryUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.openingInventoryUnit),
            sorter: (r1, r2) => r1.openingInventoryUnit - r2.openingInventoryUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'openingInventoryValue',
            key: 'openingInventoryValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.openingInventoryValue),
            sorter: (r1, r2) => r1.openingInventoryValue - r2.openingInventoryValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.QUANTITY_PURCHASED, 'plus'),
        dataIndex: ToggleableColumnIndices.QUANTITY_PURCHASED,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'quantityPurchasedUnit',
            key: 'quantityPurchasedUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.quantityPurchasedUnit),
            sorter: (r1, r2) => r1.quantityPurchasedUnit - r2.quantityPurchasedUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'quantityPurchasedValue',
            key: 'quantityPurchasedValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.quantityPurchasedValue),
            sorter: (r1, r2) => r1.quantityPurchasedValue - r2.quantityPurchasedValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.RETURNS_FROM_CUSTOMERS, 'plus'),
        dataIndex: ToggleableColumnIndices.RETURNS_FROM_CUSTOMERS,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'returnsFromCustomersUnit',
            key: 'returnsFromCustomersUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.returnsFromCustomersUnit),
            sorter: (r1, r2) => r1.returnsFromCustomersUnit - r2.returnsFromCustomersUnit,
          },
          {
            title: 'Retail value',
            width: this.retailValueColumnWidth,
            dataIndex: 'returnsFromCustomersValue',
            key: 'returnsFromCustomersValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.returnsFromCustomersValue),
            sorter: (r1, r2) => r1.returnsFromCustomersValue - r2.returnsFromCustomersValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.OTHER_ADDITION, 'plus'),
        dataIndex: ToggleableColumnIndices.OTHER_ADDITION,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'otherAdditionsUnit',
            key: 'otherAdditionsUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.otherAdditionsUnit),
            sorter: (r1, r2) => r1.otherAdditionsUnit - r2.otherAdditionsUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'otherAdditionsValue',
            key: 'otherAdditionsValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.otherAdditionsValue),
            sorter: (r1, r2) => r1.otherAdditionsValue - r2.otherAdditionsValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.QUANTITY_SOLD, 'minus'),
        dataIndex: ToggleableColumnIndices.QUANTITY_SOLD,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'quantitySoldUnit',
            key: 'quantitySoldUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.quantitySoldUnit),
            sorter: (r1, r2) => r1.quantitySoldUnit - r2.quantitySoldUnit,
          },
          {
            title: 'Retail value',
            width: this.retailValueColumnWidth,
            dataIndex: 'quantitySoldValue',
            key: 'quantitySoldValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.quantitySoldValue),
            sorter: (r1, r2) => r1.quantitySoldValue - r2.quantitySoldValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.QUANTITY_DESTROYED, 'minus'),
        dataIndex: ToggleableColumnIndices.QUANTITY_DESTROYED,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'quantityDestroyedUnit',
            key: 'quantityDestroyedUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.quantityDestroyedUnit),
            sorter: (r1, r2) => r1.quantityDestroyedUnit - r2.quantityDestroyedUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'quantityDestroyedValue',
            key: 'quantityDestroyedValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.quantityDestroyedValue),
            sorter: (r1, r2) => r1.quantityDestroyedValue - r2.quantityDestroyedValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.QUANTITY_LOST_THEFT, 'minus'),
        dataIndex: ToggleableColumnIndices.QUANTITY_LOST_THEFT,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'quantityLostUnit',
            key: 'quantityLostUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.quantityLostUnit),
            sorter: (r1, r2) => r1.quantityLostUnit - r2.quantityLostUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'quantityLostValue',
            key: 'quantityLostValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.quantityLostValue),
            sorter: (r1, r2) => r1.quantityLostValue - r2.quantityLostValue,
          },
        ],
      },
      {
        title: this.renderTitle(ToggleableColumnIndices.RETURNS_TO_AGENCY, 'minus'),
        dataIndex: ToggleableColumnIndices.RETURNS_TO_AGENCY,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'returnsToOCSUnit',
            key: 'returnsToOCSUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.returnsToOCSUnit),
            sorter: (r1, r2) => r1.returnsToOCSUnit - r2.returnsToOCSUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'returnsToOCSValue',
            key: 'returnsToOCSValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.returnsToOCSValue),
            sorter: (r1, r2) => r1.returnsToOCSValue - r2.returnsToOCSValue,
          },
        ],
      },
      {
        title: this.renderTitle('Other reductions', 'minus'),
        dataIndex: ToggleableColumnIndices.OTHER_REDUCTION,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'otherReductionsUnit',
            key: 'otherReductionsUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.otherReductionsUnit),
            sorter: (r1, r2) => r1.otherReductionsUnit - r2.otherReductionsUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'otherReductionsValue',
            key: 'otherReductionsValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.otherReductionsValue),
            sorter: (r1, r2) => r1.otherReductionsValue - r2.otherReductionsValue,
          },
        ],
      },
      {
        title: 'Closing',
        dataIndex: ToggleableColumnIndices.CLOSING,
        children: [
          {
            title: 'Qty',
            width: this.qtyWidth,
            dataIndex: 'closingInventoryUnit',
            key: 'closingInventoryUnit',
            align: 'right',
            render: (text, record) => this.renderCell('unit', record.closingInventoryUnit),
            sorter: (r1, r2) => r1.closingInventoryUnit - r2.closingInventoryUnit,
          },
          {
            title: 'Wholesale value',
            width: this.wholesaleValueColumnWidth,
            dataIndex: 'closingInventoryValue',
            key: 'closingInventoryValue',
            align: 'right',
            render: (text, record) => this.renderCell('value', record.closingInventoryValue),
            sorter: (r1, r2) => r1.closingInventoryValue - r2.closingInventoryValue,
          },
        ],
      },
    ]

    return toggleableColumns.filter(
      (toggleableColumn) => !!this.state.mappedColumns.get(toggleableColumn.dataIndex || ''),
    )
  }

  get dataSource(): CompliancePreviewTableRecord[] {
    this.negativeProducts = {}

    return (this.state.previewReport || []).map((product, index) => {
      this.populateNegativeProducts(product)

      return {
        ...product,
        key: index,
        productName: product.productName || '',
        productSKU: product.productSKU || '',
        productBarcode: product.productBarcode || '',
      }
    })
  }

  populateNegativeProducts = (product: IONFederalPreviewReport) => {
    if (this.negativeProducts.hasOwnProperty(product.productId)) {
      return
    }

    const negativeProduct: NegativeProductItem = {
      productId: product.productId,
      productName: product.productName || '',
      productSKU: product.productSKU || '',
      columns: {},
    }

    Object.keys(product).forEach((key) => {
      const amount = product[key]

      if (isNumber(amount) && amount < 0) {
        const lowercasedKey = key.toLowerCase().trim()
        const columnName = columnRecordMap[key]

        if (!negativeProduct.columns.hasOwnProperty(columnName)) {
          negativeProduct.columns[columnName] = {}
        }

        if (lowercasedKey.endsWith('unit')) {
          negativeProduct.columns[columnName].unit = amount
        }

        if (lowercasedKey.endsWith('value')) {
          negativeProduct.columns[columnName].value = amount
        }
      }
    })

    if (Object.keys(negativeProduct.columns).length > 0) {
      this.negativeProducts[product.productId] = negativeProduct
    }
  }

  render() {
    const { isLoading } = this.state

    return (
      <Table
        bordered
        loading={isLoading}
        dataSource={this.dataSource}
        className='no-background-table show-horizontal-scrollbar'
        rowClassName={() => 'background-white'}
        columns={[...this.baseColumns, ...this.toggleableColumns]}
        title={() => (
          <PreviewTableTitle
            mappedColumns={this.state.mappedColumns}
            checkedColumnIndices={this.checkedColumnIndices}
            negativeProducts={this.negativeProducts}
            setMappedColumns={(newMappedColumns: Map<string, boolean>) => {
              this.setState({ mappedColumns: newMappedColumns })
            }}
          />
        )}
        pagination={{
          position: 'both',
          showSizeChanger: true,
          hideOnSinglePage: true,
          showTotal: (total) => `Total ${total} products`,
        }}
        scroll={{
          x: true,
          y: 800,
        }}
      />
    )
  }
}
